import { X } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import { latestJobResult } from '@/modules/hiring-manager/chat/chatResults';
import { JobCard } from '@/modules/hiring-manager/chat/jobCard/JobCard';
import type { ChatMessageApi } from '@/modules/hiring-manager/chat/types';
import { HMJobEditForm } from '@/modules/hiring-manager/components/jobEditor/HMJobEditForm';
import { PortalSidePanel } from '@/modules/layouts/components/PortalSidePanel';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Callout,
  CalloutDescription,
  CalloutTitle,
} from '@/modules/ui/components/callout/Callout';

import { SavedJob } from './SavedJob';

interface ChatPanelProps {
  messages: ChatMessageApi[];
  /** Sends a card button's reply as the user's next message. */
  onSend: (text: string) => void;
  /** A turn is running: the card's buttons wait for it. */
  busy: boolean;
  /** The message being sent, so the card button that sent it shows it's working. */
  sending?: string;
  onClose: () => void;
  /** The saved job the HM asked to edit themselves: the panel shows its editor instead. */
  editingJobId?: string;
  /** A newer reply saved the job the editor shows, which still shows it as it was. */
  editorStale?: boolean;
  /** Counts the times the editor read the job again: each one opens it afresh. */
  editorVersion?: number;
  /** Reads the job into the editor again, dropping the edits made there. */
  onReloadEditor?: () => void;
  /** The editor was closed, or saved the job. */
  onEditorDone?: () => void;
  /** Opens the editor again on the job a reply saved for editing manually. */
  onEdit?: () => void;
}

/**
 * The chat's side panel: the job the chat is shaping, the one it saved, or the editor on it when the HM chose to edit
 * it themselves. The editor stays until the HM closes or saves it; otherwise the panel shows the newest reply's job.
 */
export function ChatPanel({
  messages,
  onSend,
  busy,
  sending,
  onClose,
  editingJobId,
  editorStale = false,
  editorVersion = 0,
  onReloadEditor,
  onEditorDone,
  onEdit,
}: ChatPanelProps) {
  const latest = latestJobResult(messages);
  // What the panel shows. When a reply changes it, the new one settles in so the HM notices; it shows at once when the
  // panel opens.
  const view = editingJobId
    ? `editor-${editingJobId}`
    : (latest?.messageId ?? 'empty');
  const [openedOn] = useState(view);

  return (
    <PortalSidePanel aria-label="Job" onClose={onClose}>
      <div className="flex h-12 shrink-0 items-center justify-between border-b px-5">
        <span className="text-[13px] font-semibold">Job</span>
        {editingJobId ? (
          <Button
            aria-label="Close the editor"
            onClick={onEditorDone}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        ) : (
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <div
          className={cn(
            view !== openedOn &&
              'transition-[opacity,filter] duration-200 ease-out starting:opacity-0 starting:blur-[2px]',
          )}
          key={view}
        >
          {editingJobId ? (
            <div className="flex flex-col gap-6">
              {editorStale && (
                <Callout
                  className="flex flex-col gap-2"
                  role="status"
                  variant="warning"
                >
                  <CalloutTitle>Finlay changed this job in chat</CalloutTitle>
                  <CalloutDescription>
                    The editor still shows it as it was, so saving would put
                    back what Finlay changed. Reload it to see the change; your
                    edits here would be lost.
                  </CalloutDescription>
                  <Button
                    className="self-start"
                    onClick={onReloadEditor}
                    size="sm"
                    variant="outline"
                  >
                    Reload the job
                  </Button>
                </Callout>
              )}
              <HMJobEditForm
                jobId={editingJobId}
                key={`${editingJobId}-${editorVersion}`}
                onSaved={() => onEditorDone?.()}
              />
            </div>
          ) : latest === null ? null : latest.result.type === 'job_summary' ? (
            <JobCard
              disabled={busy}
              onAction={onSend}
              sending={sending}
              summary={latest.result}
            />
          ) : (
            <SavedJob onEdit={onEdit} saved={latest.result} />
          )}
        </div>
      </div>
    </PortalSidePanel>
  );
}
